import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from './ui';
import {
  buildReportPdf, buildReportExcel, saveBlob, printPdfBlob, safeFileName, parsePageRange,
} from '../utils/reportExport';

// What the dialog says under the page box: how many pages there are, and how
// many of them are being printed.
const pagesLabel = (pages, pageCount) => {
  if (!pageCount) return '';
  const all = `${pageCount} ${pageCount === 1 ? 'page' : 'pages'} in all`;
  const want = parsePageRange(pages, pageCount);
  return want.length === pageCount ? all : `${all} · printing ${want.length} of them`;
};

// Logic behind the Share / Export dialog, shared by its desktop and mobile UI.
// A page hands over what it already shows (title, columns, rows, totals) and
// this decides the rest: PDF or Excel, portrait or landscape, preview, save,
// print, or hand the file to another app.
export function useReportShare({ title, subtitle, note, columns, rows, totalsRow, sections, summary, countText, isGroupHead, rowsPerPage, onClose }) {
  const { organiser, event } = useAuth();
  const toast = useToast();

  const [kind, setKind] = useState('pdf');              // pdf | excel
  // Portrait by default (owner's choice, 2026-10-13); Landscape is one tap away.
  const [orientation, setOrientation] = useState('portrait');
  // Text size: the last one chosen is remembered (a list printed to be READ is
  // printed large every time - owner, 2026-10-15). Large print until changed.
  const [density, setDensityState] = useState(() => {
    try { return localStorage.getItem('se_report_size') || 'large'; } catch { return 'large'; }
  });
  const setDensity = (v) => {
    setDensityState(v);
    try { localStorage.setItem('se_report_size', v); } catch { /* private window */ }
  };
  // Which pages to print ("1,3-4"): blank = all of them. The whole report is
  // always BUILT - a page only exists once the ones before it are filled - and
  // the ones not asked for are dropped at the end, so the preview shows
  // exactly what comes out of the printer.
  const [pages, setPages] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [busy, setBusy] = useState('');                 // which action is running
  const [previewUrl, setPreviewUrl] = useState('');
  const urlRef = useRef('');

  const fileName = safeFileName([event?.EventName, title, subtitle], kind === 'pdf' ? 'pdf' : 'xlsx');

  const common = {
    title, subtitle, note, columns, rows, totalsRow, sections, summary, countText, isGroupHead, rowsPerPage,
    organiserName: organiser?.OrganiserName,
    eventName: event?.EventName,
  };

  const make = useCallback(async (which = kind) => (which === 'pdf'
    ? buildReportPdf({ ...common, orientation, density, pages, onInfo: ({ pageCount: n }) => setPageCount(n) })
    : buildReportExcel(common)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [kind, orientation, density, pages, columns, rows, totalsRow, sections, summary, countText, title, subtitle, note, organiser, event]);

  // Preview is PDF-only: a spreadsheet has nothing to show until it is opened.
  useEffect(() => {
    let dead = false;
    if (kind !== 'pdf') { setPreviewUrl(''); return undefined; }
    setBusy('preview');
    make('pdf')
      .then(blob => {
        if (dead) return;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = URL.createObjectURL(blob);
        setPreviewUrl(urlRef.current);
      })
      .catch(() => { if (!dead) toast('Could not build the preview'); })
      .finally(() => { if (!dead) setBusy(''); });
    return () => { dead = true; };
  }, [kind, orientation, density, pages, make, toast]);

  useEffect(() => () => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);

  const run = async (action, fn) => {
    setBusy(action);
    try { await fn(); } catch { toast('Could not create the file'); } finally { setBusy(''); }
  };

  const save = (which = kind) => run('save', async () => {
    const blob = await make(which);
    saveBlob(blob, safeFileName([event?.EventName, title, subtitle], which === 'pdf' ? 'pdf' : 'xlsx'));
    toast(which === 'pdf' ? 'PDF saved' : 'Excel saved');
  });

  const print = () => run('print', async () => {
    printPdfBlob(await buildReportPdf({ ...common, orientation, density, pages }));
  });

  // Phones can hand the file to WhatsApp / Drive / anything through the OS
  // sheet; a desktop browser only offers it where the browser supports it.
  const canShareFile = typeof navigator !== 'undefined' && !!navigator.canShare;
  const shareFile = () => run('share', async () => {
    const blob = await make();
    const file = new File([blob], fileName, { type: blob.type });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], title, text: `${title}${subtitle ? ' — ' + subtitle : ''}` });
    } else {
      saveBlob(blob, fileName);
      toast('Saved — attach it from your downloads');
    }
  });

  return {
    kind, setKind, orientation, setOrientation, density, setDensity, busy, previewUrl, fileName,
    // Pages to print, and how many the whole report has (known once it is built).
    pages, setPages, pageCount, pageLabel: pagesLabel(pages, pageCount),
    // A multi-block report (the Brief) counts every block's rows.
    rowCount: sections ? sections.reduce((n, s) => n + (s.rows ? s.rows.length : 0), 0) : (rows || []).length,
    columnCount: sections ? sections.length : (columns || []).length,
    sections,
    title, subtitle, event, organiser,
    save, print, shareFile, canShareFile, close: onClose,
  };
}
